import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  PRODUCTS,
  defaultSelection,
  describeSelection,
  type Product,
  type VariantSelection,
} from "@/lib/shop-products";

const STORAGE_KEY = "ccl.cart.v2";
const LEGACY_STORAGE_KEY = "ccl.cart.v1";

/** Flat rate shipping to the lower 48. Free over the threshold. */
export const SHIPPING_FLAT_RATE = 9;
export const FREE_SHIPPING_THRESHOLD = 150;

export type CartLine = {
  /** Stable line key — product id plus the chosen variant values. */
  key: string;
  product: Product;
  qty: number;
  variant: VariantSelection;
  /** Human-readable variant summary ("Size: L · Color: Navy"). */
  variantLabel: string;
};

type CartEntry = {
  productId: string;
  qty: number;
  variant: VariantSelection;
};

type CartState = Record<string, CartEntry>;

type CartContextValue = {
  /** Raw line key -> entry map. */
  items: CartState;
  /** Resolved lines (unknown ids are dropped). */
  lines: CartLine[];
  count: number;
  subtotal: number;
  shipping: number;
  total: number;
  /** True once the persisted cart has been read from localStorage. */
  hydrated: boolean;
  add: (productId: string, qty?: number, variant?: VariantSelection) => void;
  setQty: (key: string, qty: number) => void;
  remove: (key: string) => void;
  clear: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);

/** Deterministic key so the same product+variant stacks into one line. */
export function lineKey(productId: string, variant?: VariantSelection): string {
  const entries = Object.entries(variant ?? {})
    .filter(([, v]) => Boolean(v))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`);
  return entries.length ? `${productId}|${entries.join(";")}` : productId;
}

function normalizeVariant(
  product: Product,
  variant?: VariantSelection,
): VariantSelection {
  if (!product.variants?.length) return {};
  const fallback = defaultSelection(product);
  const out: VariantSelection = {};
  for (const group of product.variants) {
    const requested = variant?.[group.id];
    const valid = group.options.some((o) => o.value === requested);
    const value = valid ? requested! : fallback[group.id];
    if (value) out[group.id] = value;
  }
  return out;
}

function readStored(): CartState {
  if (typeof window === "undefined") return {};
  const out: CartState = {};
  const clampQty = (qty: unknown) => {
    const n = Math.floor(Number(qty));
    return Number.isFinite(n) && n > 0 ? Math.min(n, 99) : 0;
  };

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object") {
        for (const entry of Object.values(parsed as Record<string, unknown>)) {
          if (!entry || typeof entry !== "object") continue;
          const { productId, qty, variant } = entry as Partial<CartEntry>;
          const product = PRODUCTS.find((p) => p.id === productId);
          const n = clampQty(qty);
          if (!product || !n) continue;
          const normalized = normalizeVariant(
            product,
            variant && typeof variant === "object" ? variant : undefined,
          );
          const key = lineKey(product.id, normalized);
          out[key] = { productId: product.id, qty: n, variant: normalized };
        }
        return out;
      }
    }
  } catch {
    /* fall through to legacy read */
  }

  // Migrate the pre-variant cart shape ({ productId: qty }).
  try {
    const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacy) return out;
    const parsed = JSON.parse(legacy) as unknown;
    if (!parsed || typeof parsed !== "object") return out;
    for (const [id, qty] of Object.entries(parsed as Record<string, unknown>)) {
      const product = PRODUCTS.find((p) => p.id === id);
      const n = clampQty(qty);
      if (!product || !n) continue;
      const variant = normalizeVariant(product);
      out[lineKey(product.id, variant)] = { productId: product.id, qty: n, variant };
    }
  } catch {
    /* ignore malformed legacy cart */
  }
  return out;
}

export function CartProvider({ children }: { children: ReactNode }) {
  // Start empty on both server and first client render so hydration matches;
  // the persisted cart is read in an effect right after mount.
  const [items, setItems] = useState<CartState>({});
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setItems(readStored());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* storage full / blocked — cart still works for this session */
    }
  }, [items, hydrated]);

  const add = useCallback(
    (productId: string, qty = 1, variant?: VariantSelection) => {
      const product = PRODUCTS.find((p) => p.id === productId);
      if (!product) return;
      const normalized = normalizeVariant(product, variant);
      const key = lineKey(productId, normalized);
      setItems((c) => ({
        ...c,
        [key]: {
          productId,
          variant: normalized,
          qty: Math.min(99, (c[key]?.qty ?? 0) + Math.max(1, qty)),
        },
      }));
    },
    [],
  );

  const setQty = useCallback((key: string, qty: number) => {
    setItems((c) => {
      const next = { ...c };
      const existing = next[key];
      if (!existing) return c;
      if (qty <= 0) delete next[key];
      else next[key] = { ...existing, qty: Math.min(99, Math.floor(qty)) };
      return next;
    });
  }, []);

  const remove = useCallback((key: string) => {
    setItems((c) => {
      const next = { ...c };
      delete next[key];
      return next;
    });
  }, []);

  const clear = useCallback(() => setItems({}), []);

  const value = useMemo<CartContextValue>(() => {
    const lines: CartLine[] = Object.entries(items)
      .map(([key, entry]) => {
        const product = PRODUCTS.find((p) => p.id === entry.productId);
        if (!product) return null;
        return {
          key,
          product,
          qty: entry.qty,
          variant: entry.variant ?? {},
          variantLabel: describeSelection(product, entry.variant),
        };
      })
      .filter((l): l is CartLine => l !== null)
      .sort(
        (a, b) =>
          a.product.name.localeCompare(b.product.name) ||
          a.variantLabel.localeCompare(b.variantLabel),
      );

    const count = lines.reduce((n, l) => n + l.qty, 0);
    const subtotal = lines.reduce((n, l) => n + l.product.price * l.qty, 0);
    const shipping =
      count === 0 || subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FLAT_RATE;

    return {
      items,
      lines,
      count,
      subtotal,
      shipping,
      total: subtotal + shipping,
      hydrated,
      add,
      setQty,
      remove,
      clear,
    };
  }, [items, hydrated, add, setQty, remove, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used inside <CartProvider>");
  }
  return ctx;
}

export function formatUsd(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}
